<template>
  <transition name="minimize-listing" mode="out-in">
    <HgListingCard
      v-if="!isMinimized"
      :class="[$style.listingCard]"
      :theme="theme"
      :href="hardPdpUrl"
      :isTop="isTopListing"
      :isPremium="isPremiumListing"
      :isTenantPlus="isTenantPlusListing"
      :hasVirtualTour="hasVirtualTour"
      :agencyLogoUrl="listerLogo"
      :agencyAgent="agencyAgentForListingCard"
      :imageUrl="imageUrl"
      :listingImages="listingImages"
      :address="formattedAddress"
      :price="formattedPrice"
      :priceArea="area"
      :priceInterval="interval"
      :numberOfRooms="numberOfRooms"
      :listingWithFloorSpace="listingWithFloorSpace"
      :livingSpace="livingSpace"
      :title="imageFallbackTitle"
      :description="description"
      :listingCardSize="listingCardSize"
      :isNewConstruction="isNewConstruction"
      :isNewListing="isNewListing && !isNewConstruction"
      :furnishings="furnishings"
      @click="$emit('onListCardClick', { listingItem, index, listingCardSize })"
    >
      <template #actions>
        <button :class="$style.hideListingsIcon" @click.prevent.stop="setListingHidden(true)">
          <component :is="isPlatformIs24() ? HideListingIconIs24 : HideListingIconHg" />
        </button>
      </template>
    </HgListingCard>
    <MinimizedListCard
      v-else
      :imageUrl="listingImages[0]?.url || ''"
      :numberOfRooms="numberOfRooms"
      :price="formattedPrice || ''"
      :livingSpace="livingSpace"
      @showListing="setListingHidden(false)"
    />
  </transition>
</template>

<script setup lang="ts">
import {
  HgListingCard,
  HgListingCardFurnishing,
  HgListingCardImage,
  HgListingCardSize,
  HgSupportedLanguage,
} from '@smg-real-estate/domus';
import { AreaThePriceRefersTo, Characteristics, OfferType } from '@smg-real-estate/hg-rets-schema';

import {
  AgencyAgent,
  getListingSpace,
  isListingWithFloorSpace,
  ListingCardSize,
  ListingItem,
  ListingType,
  OfferType as OfferTypeWithLowercase,
  SearchModel,
} from '@smg-real-estate/hg-search-libs';

import { computed, defineAsyncComponent, inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { InteractionGoal } from '@/helpers/GTM/GTMParameters';
import {
  getBuyPrice,
  getDisplayingListingImages,
  getListingAddress,
  getListingDefaultLocale,
  getListingImage,
  getListingVirtualTour,
  getNewListingState,
  getRentGross,
} from '@/helpers/ListingData/ListingData';
import { getCurrentPlatformTheme, isPlatformIs24 } from '@/helpers/platformDetection';
import {
  addHiddenListing,
  isListingHidden,
  removeHiddenListing,
} from '@/helpers/StorageHelper/searchResultStorage';

import { trackHideListingInteraction } from '@/components/ListCard/ListCard.tracking';
import MinimizedListCard from '@/components/MinimizedListCard/MinimizedListCard.vue';
import useFeatureToggle, { FeatureToggles } from '@/composables/useFeatureToggle';
import { getLayoutSolutionRequestQueryParameters } from '@/resultlist/layoutSolutionRequest';
import { detailPageRouteMap } from '@/router/layoutSolutionRoutes';
import { allowedFurnishingFields } from './models';

const HideListingIconIs24 = defineAsyncComponent(
  () => import('@/assets/icons/EyesVisibilityOffIs24.svg?component'),
);
const HideListingIconHg = defineAsyncComponent(
  () => import('@/assets/icons/EyesVisibilityOffHg.svg?component'),
);

const PRICE_AREA_LABELS = {
  [AreaThePriceRefersTo.All]: undefined,
  [AreaThePriceRefersTo.M2]: 'm',
  [AreaThePriceRefersTo.Km2]: 'km',
};

interface Props {
  listingItem: ListingItem;
  index: number;
}

const props = defineProps<Props>();
const emit = defineEmits(['moreImagesClick', 'blurredImageDisplayed', 'onListCardClick']);

const searchModel = inject<SearchModel>('searchModel');

const store = useStore();
const { locale, t } = useI18n();
const route = useRoute();
const showIsNewBadgeToggleOn = useFeatureToggle(FeatureToggles.SHOW_IS_NEW_BADGE);

const isTopListing = ref(props.listingItem.listingType?.type === ListingType.TOP);
const isPremiumListing = ref(props.listingItem.listingType?.type === ListingType.PREMIUM);
const theme = ref(getCurrentPlatformTheme());
const isNewListing = ref(false);
const isMinimized = ref(false);

const fullSearchResult = computed(() => store.getters['resultList/fullSearchResult']);
const fallbackSearchResult = computed(() => store.getters['resultList/fallbackSearchResult']);
const fallbackSearchType = computed(() => store.getters['resultList/fallbackSearchType']);

const isNewConstruction = computed(() => !!props.listingItem.newConstructionData);
const listingId = computed(() => props.listingItem.listing.id);
const listingCardSize = computed<HgListingCardSize>((): HgListingCardSize => {
  if (props.listingItem.listingCard?.size === ListingCardSize.SMALL) {
    return HgListingCardSize.XS;
  }
  return HgListingCardSize[props.listingItem.listingCard?.size || ListingCardSize.LARGE];
});
const isTenantPlusListing = computed<boolean>(
  () => props.listingItem.listing.valueAddedServices?.isTenantPlusListing ?? false,
);
const formattedAddress = computed<string>(() => getListingAddress(props.listingItem.listing) || '');
const numberOfRooms = computed<number | undefined>(() => {
  if (!isListingWithFloorSpace(props.listingItem.listing)) {
    // non-commercial
    return props.listingItem.listing.characteristics?.numberOfRooms;
  }

  if (!livingSpace.value) {
    // fallback: commercial but without living space
    return props.listingItem.listing.characteristics?.numberOfRooms;
  }
  return undefined;
});
const listingDefaultLocale = computed(() => getListingDefaultLocale(props.listingItem.listing));
const listingForCurrentLocale = computed(
  () => props.listingItem.listing.localization?.[locale.value as HgSupportedLanguage],
);
const description = computed((): string | undefined => {
  if (isPlatformIs24()) {
    return (
      listingForCurrentLocale.value?.text?.description ??
      listingDefaultLocale.value?.text?.description
    );
  }
  return listingDefaultLocale.value?.text?.description;
});
const listingWithFloorSpace = computed(() => isListingWithFloorSpace(props.listingItem.listing));
const livingSpace = computed(() => getListingSpace(props.listingItem.listing));
const offerType = computed(() => props.listingItem.listing.offerType);
const prices = computed(() => props.listingItem.listing.prices);
const imageFallbackTitle = computed((): string | undefined => {
  if (isPlatformIs24()) {
    return listingForCurrentLocale.value?.text?.title ?? listingDefaultLocale.value?.text?.title;
  }
  return listingDefaultLocale.value?.text?.title;
});
const hasVirtualTour = computed(() => !!getListingVirtualTour(listingDefaultLocale.value));
const listerLogo = computed(() => props.listingItem.listerBranding?.logoUrl);
const area = computed<string | undefined>(() => {
  if (offerType.value === OfferType.Rent && prices.value?.rent) {
    return PRICE_AREA_LABELS[prices.value?.rent.area as AreaThePriceRefersTo]; // convert e.g. 'M2', 'KM2'
  }

  return undefined;
});

const interval = computed<string | undefined>(() => {
  if (offerType.value === OfferType.Rent && prices.value?.rent) {
    return prices.value?.rent.interval; // e.g. 'MONTH', 'YEAR'
  }

  if (offerType.value === OfferType.Buy && prices.value?.buy) {
    // Property 'interval' does not exist on type 'Buy'.
    const buyPrice = prices.value.buy as { interval?: string };
    return buyPrice.interval; // e.g. 'MONTH', 'YEAR'
  }

  return undefined;
});

const price = computed<string | undefined>(() => {
  if (!prices.value) {
    return;
  }

  switch (offerType.value) {
    case OfferType.Rent:
      return getRentGross(prices.value, locale.value);
    case OfferType.Buy:
      return getBuyPrice(prices.value, locale.value);
    default:
      return undefined;
  }
});

const formattedPrice = computed<string | undefined>(() =>
  price.value && prices.value?.currency ? `${prices.value?.currency} ${price.value}` : undefined,
);
const imageUrl = computed((): string | undefined => {
  const res = getListingImage(props.listingItem.listing, 0);
  return res ? res.url : '';
});
const listingImages = computed<HgListingCardImage[]>(() =>
  getDisplayingListingImages(props.listingItem.listing),
);

const furnishings = computed<HgListingCardFurnishing[]>(() => {
  const characteristics: Characteristics = props.listingItem.listing.characteristics ?? {};
  return allowedFurnishingFields
    .filter(({ name }) => !!characteristics[name])
    .map((furnishing) => ({
      ...furnishing,
      title: t(`furnishings.fields.${furnishing.name}`),
    }));
});

const agencyAgentForListingCard = computed<AgencyAgent | undefined>(() => {
  if (
    !!props.listingItem.listerBranding?.isPremiumBranding &&
    props.listingItem.listing.offerType === OfferType.Buy &&
    props.listingItem.agencyAgent
  ) {
    return props.listingItem.agencyAgent;
  }
  return undefined;
});

const hardPdpUrl = computed<string>(() => {
  const id = props.listingItem.listing.id;
  const type = (props.listingItem.listing.offerType?.toLowerCase() ||
    OfferType.Rent) as OfferTypeWithLowercase;
  const currentLocale = locale.value as HgSupportedLanguage;

  // Get localized path segment or fallback to English
  const path = detailPageRouteMap[currentLocale][type] || detailPageRouteMap.en[type];

  let url = `/${path}/${id}`;

  const params = new URLSearchParams();
  const layoutSolutionParams = getLayoutSolutionRequestQueryParameters(route.query);
  for (const [key, value] of Object.entries(layoutSolutionParams)) {
    params.append(key, value as string);
  }

  return `${url}?${params.toString()}`;
});

onMounted(async () => {
  // TODO Does SHOW_IS_NEW_BADGE feature flag is still needed?
  isNewListing.value =
    getNewListingState(props.listingItem.listing) || showIsNewBadgeToggleOn.value;
  isMinimized.value = listingId.value ? isListingHidden(listingId.value) : false;
});

const setListingHidden = (hidden: boolean) => {
  if (!listingId.value) {
    return;
  }

  const trackingMeta = {
    listingItem: props.listingItem,
    positionOverall: props.index + 1,
    listingCardSize: listingCardSize.value,
    searchModel: searchModel as SearchModel,
  };

  if (hidden) {
    addHiddenListing(listingId.value);
    trackHideListingInteraction(trackingMeta, InteractionGoal.HIDE_LISTING_SUCCESS);
  } else {
    removeHiddenListing(listingId.value);
    trackHideListingInteraction(trackingMeta, InteractionGoal.UNHIDE_LISTING);
  }

  isMinimized.value = hidden;
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.hideListingsIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: $space-50;
  margin-right: $space-50;
  line-height: 0;
  color: $color-grey-100;
  cursor: pointer;
  background-color: $color-white;
  border-radius: 100%;
  outline: none;

  @media (min-width: $viewport-md-min) {
    width: 2.625rem;
    height: 2.625rem;
    border: 1px solid var(--card-favourites-border-color);
  }
}

:global(.minimize-listing-enter-active),
:global(.minimize-listing-leave-active) {
  transition: all 0.3s ease;
}

:global(.minimize-listing-enter),
:global(.minimize-listing-leave-to) {
  opacity: 0;
  transform: translateY(10px);
}

.listingCard {
  // Targeting the image slider item within HgListingCard
  // 'splide__list' is a unscoped class name, hence it does not work with scoped styles
  ul[class='splide__list'] > li {
    padding: 0;
  }

  [class*='HgSlider_splide_'] {
    visibility: visible;
  }

  li[class*='HgSlider_splideSlideCenter_'] {
    width: calc(100% + 0px);
    margin-right: 10px;
  }
}
</style>
